import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Box, Flex, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import colors from '../../styles/theme/foundation/colors';
import LoadingSpinner from '../Spinner';

import TabButton from './TabButton';

const Tabs = ({ children, layerStyle, hasMore, fetchNext, collapsed, height = 'auto' }) => {
  const { activeTab } = useParams();
  const [currentTab, setCurrentTab] = useState({ title: '', tabId: '', main: null });
  const history = useHistory();
  const { url } = useRouteMatch();

  useEffect(() => {
    const defaultTab = children.find(child => child.props.tabId === activeTab);
    const initialTab = defaultTab ? defaultTab.props : children.find(child => !child.props.component).props;

    if (!activeTab) {
      history.push(`${url}/${initialTab.tabId}`);
    }

    setCurrentTab(initialTab);
  }, [activeTab]);

  const handleTabClick = clickedTab => {
    if (clickedTab.tabId !== activeTab) {
      history.push(clickedTab.tabId);
    }

    if (clickedTab.onTabSelect) {
      clickedTab?.onTabSelect();
    }

    setCurrentTab(clickedTab);
  };

  const tabs = children.map((child, idx) => {
    if (!child.props.title && !child.props.description) {
      const CustomTab = child.props.component;
      return <CustomTab />;
    }

    return (
      <TabButton
        collapsed={collapsed}
        cypressId={child.props?.cypressId}
        dataAnalyticsId={child.props?.dataAnalyticsId}
        description={child.props.description}
        id={child.props?.id}
        idx={idx}
        isActive={child.props.title === currentTab.title && child.props.id === currentTab.id}
        key={child.props.id}
        leftIcon={child.props.icon}
        subMenu={child.props?.subMenu}
        title={child.props.title}
        onClick={() => handleTabClick(child.props)}
      />
    );
  });

  if (!currentTab.title) return <LoadingSpinner />;

  const tabPanel = children.find(child => child.props.tabId === currentTab.tabId).props;
  const TabContent = tabPanel.main;
  const TabHeader = tabPanel.header;

  return (
    <Flex
      alignItems="flex-start"
      height="100%"
      justifyContent="space-between"
      minW="710px"
      overflowX="hidden"
      w="auto"
    >
      <Box
        height={height}
        layerStyle="tabPanel"
        marginTop={0}
        maxWidth="300px"
        overflowY="auto"
      >
        <InfiniteScroll
          dataLength={tabs.length}
          hasMore={hasMore}
          height={height}
          loader={<Text color={colors.greyScale['600']} textStyle="bodySm">Loading...</Text>}
          maxHeight="80%"
          next={fetchNext}
        >
          <Box>
            {tabs}
          </Box>
        </InfiniteScroll>
      </Box>

      <Box
        className="template-tab-content"
        flex={1}
        layerStyle={layerStyle}
        maxHeight="100%"
        ml={4}
        overflow="auto"
        width="100%"
      >
        <TabHeader />
        <TabContent dusk={tabPanel.dusk} />
      </Box>
    </Flex>
  );
};

const Tab = ({ children }) => <>{children}</>;

Tabs.defaultProps = {
  layerStyle: 'container',
  collapsed: false
};

Tabs.propTypes = {
  children: PropTypes.arrayOf(PropTypes.shape({
    props: PropTypes.shape({
      dataAnalyticsId: PropTypes.string,
      description: PropTypes.string,
      title: PropTypes.string,
      icon: PropTypes.func
    })
  })),
  collapsed: PropTypes.bool,
  layerStyle: PropTypes.string,
  hasMore: PropTypes.bool,
  fetchNext: PropTypes.func
};

Tab.propTypes = { children: PropTypes.element };

export { Tab, Tabs };
export default Tabs;
